import React, { useEffect, useState, useRef, useCallback, useMemo, useContext } from 'react';
import ArrowBack from '@mui/icons-material/ArrowBack';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import {
  postWithdrawMoney,
  useClientBank,
  useClientDetails,
  useClientStats,
  useClientTaxReportStatus,
  useMetalsLedger,
  downloadMetalsLedgerExcel,
  GOLD_TRANSACTION_PURPOSES,
  SILVER_TRANSACTION_PURPOSES,
  getMetalSipDetails,
} from './useClientDetails';
import { getRupee } from '../../utils/numbers';
import Avatar from '@mui/material/Avatar';
import Dialog from '@mui/material/Dialog';
import Skeleton from '@mui/material/Skeleton';
import ClientInvestmentsTable from './ClientInvestmentsTable';
import TableSkeleton from '../../components/TableSkeleton';
import { useDelay } from '../../hooks/useDelay';
import { useClientVirtualAccount } from '../Clients/useClient';
import { debounce } from '../../utils/debounce';
import { SearchOutlined } from '@mui/icons-material';
import Input from '../../components/Fields/Input';
import { useDownloadCsv } from '../../hooks/useDownloadCsv';
import ReinvestModal from '../../components/organisms/ReinvestModal/ReinvestModal';
import { getCreateWalletPaymentLink } from '../../hooks/useGetPartnerProfile';
import { getPgInformation } from '../../hooks/useGetPgInformation';
import {
  PAYMENT_GATEWAY_IDFC,
  clearStoredPgInformation,
  getStoredConvenienceFee,
  getStoredPaymentGateway,
  savePgInformation,
} from '../../utils/pgInformation';
import { Button, CircularProgress, DialogContent, DialogTitle, Tab, Tabs } from '@mui/material';
import {
  AddCircleIcon,
  CloseOutlinedIcon,
  InfoIcon,
  DownloadForOfflineOutlinedIcon,
  AccountBalanceWalletOutlinedIcon,
} from '../../assets/mui';
import PlayForWorkIcon from '@mui/icons-material/PlayForWork';
import toast from 'react-hot-toast';
import { CLIENT_DETAIL_EVENTS } from './constants';
import { PATH } from '../../services/NetworkManager';
import { useAnalytics } from '../../hooks/useAnalytics';
import CallMadeIcon from '@mui/icons-material/CallMade';
import {
  downloadPartnerInvestmentsCSV,
  useGoldInvestments,
  useInvestments,
} from '../Investments/useInvestments';
import TransactionList from './TransactionList';
import { useProfile } from '../../context/ProfileContext';
import DigitalMetalSipTransactionsTable from './DigitalMetalSipTransactionsTable';
import TransactionTypeDropdown from '../../components/TransactionTypeDropdown';
import { RemoteConfigContext } from '../../context/RemoteConfigContext';

interface ClientDetailProps {}

const ClientDetail: React.FC<ClientDetailProps> = () => {
  const { profileData } = useProfile();
  const [open, setOpen] = useState(false);
  const [viewBeneficiaryOpen, setViewBeneficiaryOpen] = useState(false);
  const [walletDetailsOpen, setWalletDetailsOpen] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(0);
  // update here if in future we implement filters
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [fromDate] = useState('2022-01-01T06:07:42.132Z');
  const [toDate] = useState(new Date().toISOString());
  const [sortByUpdatedAtDesc] = useState(true);
  const [transactionTypes] = useState(['CREDIT', 'DEBIT']);
  const [TransactionState] = useState(['INITIATED', 'SUCCESS', 'FAILED', 'REVERSED', 'DELETED']);
  const [search, setSearch] = useState('');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('');
  const [addWalletOpen, setAddWalletOpen] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawWalletOpen, setWithdrawWalletOpen] = useState(false);
  const [tab, setTab] = useState('INVOICE_DISCOUNTING');
  const [amount, setAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [userPaymentLink, setUserPaymentLink] = useState('');
  const [convenienceFee, setConvenienceFee] = useState(getStoredConvenienceFee);
  const [paymentGateway, setPaymentGateway] = useState(getStoredPaymentGateway);
  const [isTopUpAllowed, setIsTopUpAllowed] = useState(true);
  const addWalletOpenRef = useRef(false);
  const [metalSipDetails, setMetalSipDetails] = useState<any>(null);
  const [metalSipDetailsLoading, setMetalSipDetailsLoading] = useState(false);
  const [taxReportModalOpen, setTaxReportModalOpen] = useState(false);
  const [selectedFY, setSelectedFY] = useState(2025);
  const { clientId } = useParams();
  const { data, isLoading: dataLoading } = useClientDetails(clientId);
  const {
    data: statsData,
    isLoading: statsLoading,
    refetch: statsRefetch,
  } = useClientStats(clientId);
  const { data: bankData, isLoading: isBankLoading } = useClientBank(clientId);

  const { track } = useAnalytics();
  const isInitialRender = useRef(true);
  const { remoteConfig } = useContext(RemoteConfigContext);
  const isOnlinePaymentEnabled = remoteConfig?.onlinePaymentEnable;

  const getInvestmentValue = ({ tab, profileData }) => {
    return tab === 'INVOICE_DISCOUNTING' && profileData?.productTypes?.enableInvoiceDiscounting
      ? 'INVOICE_DISCOUNTING'
      : tab === 'BOND' && profileData?.productTypes?.enableBond
        ? 'BOND'
        : tab === 'ASSET_LEASING' && profileData?.productTypes?.enableAssetLeasing
          ? 'ASSET_LEASING'
          : tab === 'TAP_RESERVE' && profileData?.productTypes?.enableTapReserve
            ? 'TAP_RESERVE'
            : profileData?.productTypes?.enableWallet
              ? 'WALLET'
              : 'INVOICE_DISCOUNTING';
  };

  const { data: investmentsData, isLoading: investmentsLoading } = useInvestments({
    payload: {
      pageNo: pageNumber,
      pageSize,
      orderBy,
      order,
      search,
      filters: [
        {
          field: 'productType',
          operation: 'EQUALS',
          value: getInvestmentValue({ tab, profileData }),
        },
        {
          field: 'clientPublicId',
          operation: 'EQUALS',
          value: clientId,
        },
      ],
    },
  });

  const { data: goldInvestmentsData, isLoading: goldInvestmentsLoading } = useGoldInvestments({
    clientId,
    pageNumber,
    pageSize,
    fromDate,
    toDate,
    sortByUpdatedAtDesc,
    transactionTypes,
    TransactionState,
    search,
  });
  const { data: metalsLedgerData, isLoading: metalsLedgerLoading } = useMetalsLedger({
    clientId,
    tab,
    page: pageNumber,
    pageSize,
    sortBy: 'id',
    sortDir: 'DESC',
    searchPhrase: '',
  });
  const currentData = tab === 'WALLET' ? goldInvestmentsData : investmentsData;
  const currentLoading = tab === 'WALLET' ? goldInvestmentsLoading : investmentsLoading;
  const { data: virtualAccountData, isLoading: virtualAccountLoading } =
    useClientVirtualAccount(clientId);
  const [csvLoading, setCsvLoading] = useState(false);

  const downloadMetalsLedgerExcelFile = async () => {
    const metalType = tab === 'DIGITAL_GOLD_SIP' ? 'GOLD' : 'SILVER';
    const transactionPurposes =
      tab === 'DIGITAL_GOLD_SIP' ? GOLD_TRANSACTION_PURPOSES : SILVER_TRANSACTION_PURPOSES;

    const response = await downloadMetalsLedgerExcel({
      clientId,
      metalType,
      transactionPurposes,
    });

    const downloadUrl = response?.URL || response?.data?.URL;
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    } else {
      toast.error('Failed to get download URL');
    }
  };
  useEffect(() => {
    clearStoredPgInformation();
    setConvenienceFee(getStoredConvenienceFee());
    setPaymentGateway(getStoredPaymentGateway());
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    if (tab === 'DIGITAL_GOLD_SIP' || tab === 'DIGITAL_SILVER_SIP') {
      const fetchMetalSipDetails = async () => {
        setMetalSipDetailsLoading(true);
        try {
          const response = await getMetalSipDetails({ clientPublicId: clientId });
          setMetalSipDetails(response?.data || response);
        } catch (error) {
          toast.error('Failed to fetch metal SIP details:', error);
        } finally {
          setMetalSipDetailsLoading(false);
        }
      };
      fetchMetalSipDetails();
    }
  }, [clientId, tab]);

  const downloadCsv = async () => {
    setCsvLoading(true);

    try {
      if (tab === 'DIGITAL_GOLD_SIP' || tab === 'DIGITAL_SILVER_SIP') {
        await downloadMetalsLedgerExcelFile();
      } else {
        // Handle other investment types
        const response = await downloadPartnerInvestmentsCSV({
          pageNo: pageNumber,
          pageSize,
          orderBy,
          order,
          search,
          filters: [
            {
              field: 'productType',
              operation: 'EQUALS',
              value: getInvestmentValue({ tab, profileData }),
            },
            {
              field: 'clientPublicId',
              operation: 'EQUALS',
              value: clientId,
            },
          ],
        });
        window.open(response?.data, '_blank');
      }
    } catch (error) {
      toast.error('Failed to download file. Please try again.');
    } finally {
      setCsvLoading(false);
    }
  };

  const { data: taxReportStatus } = useClientTaxReportStatus(clientId, selectedFY);
  const { alTaxReportsGenerated, idTaxReportGenerated } = taxReportStatus?.data || {};

  const getTaxReportPath = () => {
    let pathNames = '';

    if (alTaxReportsGenerated) {
      pathNames = pathNames + '&financeType=ASSET_LEASING';
    }

    if (idTaxReportGenerated && alTaxReportsGenerated) {
      pathNames = pathNames + ',INVOICE_DISCOUNTING';
    } else if (!alTaxReportsGenerated && idTaxReportGenerated) {
      pathNames = pathNames + '&financeType=INVOICE_DISCOUNTING';
    }

    return `${PATH.taxReportDownload
      .replace('{{clientId}}', clientId)
      .replace('{{year}}', String(selectedFY))}${pathNames}`;
  };

  const { csvLoading: taxReportLoading, downloadCsv: downloadTaxReport } = useDownloadCsv({
    path: getTaxReportPath(),
    filename:
      alTaxReportsGenerated && idTaxReportGenerated
        ? `tax_report_FY${selectedFY}.zip`
        : `tax_report_FY${selectedFY}.xlsx`,
  });

  const isReady = useDelay(dataLoading, 200);
  const isStatsReady = useDelay(statsLoading, 500);
  const isInvestmentsReady = useDelay(investmentsLoading, 500);

  const { aadhar: aadhaar, email, name, pan, phone, familyName, clientPublicId } = data?.data || {};

  const { activeInvestment, totalInvestment, totalPayout, walletBalance } = statsData?.data || {};

  const { accountNumber, ifscCode } = bankData?.data || {};
  const navigate = useNavigate();
  const {
    accountNumber: walletAccountNumber,
    bankName: walletBankName,
    branchDetails: walletBranchDetails,
    ifscCode: walletIfscCode,
  } = virtualAccountData?.data || {};

  const handleOpen = () => setOpen(true);

  const handleClose = () => setOpen(false);

  const handleViewBeneficiaryOpen = useCallback(() => {
    track({ eventName: CLIENT_DETAIL_EVENTS.Client_View_beneficiary });
    setViewBeneficiaryOpen(true);
  }, [track]);

  const handleViewBeneficiaryClose = () => setViewBeneficiaryOpen(false);

  const handleWalletDetailsOpen = () => setWalletDetailsOpen(true);

  const handleWalletDetailsClose = () => {
    // setting both the amount fields to empty on close of bank info modal
    setAmount('');
    setWithdrawAmount('');
    setWalletDetailsOpen(false);
  };

  const handlePaymentClose = () => {
    setIsPaymentOpen(false);
    setUserPaymentLink('');
  };

  // convenience fee / gateway for this client's top-up, persisted for the payment screens.
  // A failure here must not block the top-up, so it is handled separately.
  const fetchPgInformation = useCallback(
    async (topUpAmount: number | string) => {
      try {
        const pgInformationResponse = await getPgInformation({
          clientPublicId: clientId,
          amount: topUpAmount || 0,
          paymentMode: 'UPI',
        });
        const pgInformation = pgInformationResponse?.data;
        savePgInformation(pgInformation);
        setConvenienceFee(getStoredConvenienceFee());
        setPaymentGateway(getStoredPaymentGateway());

        if (!addWalletOpenRef.current || Number(topUpAmount) <= 0) return;

        const isAllowed = pgInformation?.buttonEnabled !== false;
        setIsTopUpAllowed(isAllowed);
        if (!isAllowed && pgInformation?.message) {
          toast.error(pgInformation.message, {
            duration: 3500,
            position: 'top-center',
          });
        }
      } catch (pgInformationError) {
        console.error('Failed to fetch pg information', pgInformationError);
      }
    },
    [clientId],
  );

  const debouncedFetchPgInformation = useMemo(
    () => debounce((topUpAmount: string) => fetchPgInformation(topUpAmount), 600),
    [fetchPgInformation],
  );

  useEffect(() => {
    if (!addWalletOpen) return;
    if (!amount || Number(amount) === 0) {
      setIsTopUpAllowed(true);
      return;
    }
    debouncedFetchPgInformation(amount);
  }, [amount, addWalletOpen, debouncedFetchPgInformation]);

  const handleAddWalletBalanceOpen = useCallback(() => {
    track({ eventName: CLIENT_DETAIL_EVENTS.Client_Wallet_Add_Money });
    addWalletOpenRef.current = true;
    setAddWalletOpen(true);
    setIsTopUpAllowed(true);
    fetchPgInformation(0);
  }, [track, fetchPgInformation]);

  const handleAddWalletBalanceClose = () => {
    addWalletOpenRef.current = false;
    setAddWalletOpen(false);
    setAmount('');
    setIsTopUpAllowed(true);
  };

  const handleWithdrawWalletBalanceOpen = useCallback(() => {
    track({ eventName: CLIENT_DETAIL_EVENTS.Client_Wallet_Withdraw });
    setWithdrawWalletOpen(true);
  }, [track]);

  const handleWithdrawWalletBalanceClose = () => {
    setWithdrawAmount('');
    setWithdrawWalletOpen(false);
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    const onlyNumbers = value.replace(/[^0-9]/g, '');
    if (onlyNumbers.length > 7) {
      toast.error('Amount should be less than 1cr', {
        duration: 2000,
        position: 'top-center',
      });
      return;
    }
    setAmount(onlyNumbers);
  };

const handleWithdrawAmountChange = (e) => {
  const value = e.target.value;

  // Allow digits and a single decimal point
  let onlyNumbers = value.replace(/[^0-9.]/g, '');

  // Prevent multiple decimal points (keep only the first one)
  let parts = onlyNumbers.split('.');
  if (parts.length > 2) {
    onlyNumbers = parts[0] + '.' + parts.slice(1).join('');
    parts = onlyNumbers.split('.');
  }

  // Limit to 2 decimal places
  if (parts.length === 2 && parts[1].length > 2) {
    onlyNumbers = parts[0] + '.' + parts[1].slice(0, 2);
  }

  // Skip balance check while the value is incomplete (e.g. "12." or "")
  if (onlyNumbers === '' || onlyNumbers === '.') {
    setWithdrawAmount(onlyNumbers);
    return;
  }

  if (parseFloat(onlyNumbers) > walletBalance) {
    toast.error('Entered Amount is more than wallet balance', {
      duration: 2000,
      position: 'top-center',
    });
    return;
  }

  setWithdrawAmount(onlyNumbers);
};
  const handleChangePage = (event, newPage) => {
    setPageNumber(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPageNumber(0);
  };

  // withdraw money from wallet api call
  const handleWithdrawMoney = () => {
    track({ eventName: CLIENT_DETAIL_EVENTS.Client_Wallet_Withdraw });
    setWithdrawLoading(true);
    postWithdrawMoney({ clientPublicId: clientId, amount: withdrawAmount })
      .then((res) => {
        track({
          eventName: CLIENT_DETAIL_EVENTS.Client_Wallet_Withdraw_Status,
          eventProperties: {
            status: 'success',
          },
        });
        toast.success(`${res?.message || 'Withdrawal request initiated successfully'}`, {
          duration: 2000,
          position: 'top-center',
        });
      })
      .catch((err) => {
        track({
          eventName: CLIENT_DETAIL_EVENTS.Client_Wallet_Withdraw_Status,
          eventProperties: { status: 'failure' },
        });
        toast.error(
          `${err?.response?.data?.message || 'Something went wrong, please try again later'}`,
          {
            duration: 2500,
            position: 'top-center',
          },
        );
      })
      .finally(() => {
        handleWalletDetailsClose();
        handleWithdrawWalletBalanceClose();
        statsRefetch();
        setWithdrawLoading(false);
      });
  };

  const handleGenerateLink = async () => {
    try {
      if (!isOnlinePaymentEnabled) {
        setTimeout(() => {
          handleAddWalletBalanceClose();
        }, 2000);

        toast.error('Online payments down. Please use bank transfer', {
          duration: 3500,
          position: 'top-center',
        });

        return handleWalletDetailsOpen();
      }

      track({ eventName: CLIENT_DETAIL_EVENTS.Client_Wallet_Generate_Link });

      const response = await getCreateWalletPaymentLink({
        clientPublicId: clientId,
        amount: amount,
      });

      setUserPaymentLink(response?.data);
      setIsPaymentOpen(true);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || 'Something went wrong, please try again later';
      toast.error(errorMessage, {
        duration: 2500,
        position: 'top-center',
      });
    }
  };

  const handleChangeSort = (e, columnId) => {
    if (columnId === orderBy) {
      setOrder(order === 'desc' ? 'asc' : 'desc');
    }
    setOrderBy(columnId);
  };

  const copyText = `Please add money to the following beneficiary to make investments on Tap Invest. (Note that money should be deposited from a bank account matching the name below.):
    Name: Fixdot Fintech Private Limited
    Bank Name: ${walletBankName}
    Branch Name: ${walletBranchDetails}
    Account Number: ${walletAccountNumber}
    IFSC Code: ${walletIfscCode}`;

  const waText = `Please add money to the following beneficiary to make investments on Tap Invest. (Note that money should be deposited from a bank account matching the name below.): %0a
    Name: Fixdot Fintech Private Limited %0a
    Bank Name: ${walletBankName} %0a
    Branch Name: ${walletBranchDetails} %0a
    Account Number: ${walletAccountNumber} %0a
    IFSC Code: ${walletIfscCode}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(copyText);
    toast.success('Copied to clipboard', {
      duration: 2000,
      position: 'top-center',
    });
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const debounceHandleSearch = debounce(handleSearch, 500);

  const handleBackClick = () => {
    window.history.back();
  };

  const handleTabChange = (newValue) => {
    setTab(newValue);
    setPageNumber(0);
    setSearch('');
    setTransactionTypeFilter('');
    track({
      eventName: CLIENT_DETAIL_EVENTS.Client_Tab_Change,
      eventProperties: { tab: newValue },
    });
  };

  useEffect(() => {
    if (isInitialRender.current) {
      track({ eventName: CLIENT_DETAIL_EVENTS.Client_Overview });
      //for the first time, we need to track the tab change
      track({
        eventName: CLIENT_DETAIL_EVENTS.Client_Tab_Change,
        eventProperties: { tab: 'INVOICE_DISCOUNTING' },
      });
      isInitialRender.current = false;
    }
  }, []);

  // Memoize derived values
  const taxReportPath = useMemo(() => {
    return getTaxReportPath();
  }, [clientId, alTaxReportsGenerated, idTaxReportGenerated]);

  // Memoize child component props
  const clientInvestmentsTableProps = useMemo(
    () => ({
      data: investmentsData,
      pageSize,
      pageNo: pageNumber,
      orderBy,
      order,
      handleChangePage,
      handleChangeRowsPerPage,
      handleChangeSort,
      tab: getInvestmentValue({ tab, profileData }),
    }),
    [
      investmentsData,
      pageSize,
      pageNumber,
      orderBy,
      order,
      handleChangePage,
      handleChangeRowsPerPage,
      handleChangeSort,
      tab,
      profileData,
    ],
  );
  return (
    <div className="my-6 max-w-7xl mx-auto">
      {/* Heading */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBackClick}
            className="bg-green-700/10 flex items-center justify-center p-1.5 h-9 w-9 rounded-full text-green-700"
          >
            <ArrowBack fontSize="small" />
          </button>
          <h1 className="text-lg md:text-xl font-semibold">Client Overview</h1>
        </div>
        {/* TODO: Uncomment when Create Order flow is ready */}
        {/* <button
                    type="button"
                    className="bg-green-700 flex items-center gap-2 text-sm disabled:bg-green-700/80 text-white rounded-xl font-bold h-10 w-fit px-4"
                >
                    <AddCircle fontSize="small" />
                    Create New Order
                </button> */}
        <div className="flex gap-4">
          <button
            type="button"
            className="bg-white shadow flex items-center gap-2 text-sm text-gray-700 border border-gray-300 rounded-xl font-semibold h-10 w-fit px-4"
            onClick={() => setTaxReportModalOpen(true)}
          >
            <DownloadForOfflineOutlinedIcon fontSize="small" />
            Tax Report
          </button>
          <button
            onClick={handleViewBeneficiaryOpen}
            type="button"
            className="bg-white shadow flex items-center gap-2 text-sm text-gray-700 border border-gray-300 rounded-xl font-semibold h-10 w-fit px-4"
          >
            <FilterListRoundedIcon fontSize="small" />
            View Beneficiary Details
          </button>
          <button
            onClick={() => {
              handleWalletDetailsOpen();
              track({
                eventName: CLIENT_DETAIL_EVENTS.Client_Wallet_Balance_Selected,
              });
            }}
            type="button"
            className="bg-green-700 shadow flex items-center gap-2 text-sm text-white border border-green-900 rounded-xl font-semibold h-10 w-fit px-4"
          >
            <AccountBalanceWalletOutlinedIcon fontSize="small" />
            Wallet Balance
          </button>
          {familyName && (
            <button
              onClick={() => {
                navigate(`/family-account/${clientPublicId}`);
              }}
              type="button"
              className="bg-green-700 shadow flex items-center gap-2 text-sm text-white border border-green-900 rounded-xl font-semibold h-10 w-fit px-4"
            >
              Family Account
              <CallMadeIcon fontSize="small" />
            </button>
          )}
        </div>
      </div>

      {/* User Details */}
      <div className="bg-black/[0.04] min-h-20 grid grid-cols-6 gap-6 sm:gap-3 lg:gap-2 rounded-lg px-6 py-4 mt-6">
        {/* Name/PAN */}
        <div className="col-span-6 max-lg:mb-6 lg:col-span-2 flex items-center gap-3">
          {name && (
            <Avatar
              sizes=""
              className="uppercase md:!w-14 md:!h-14 !bg-white/80 !text-slate-600 border border-slate-300"
            >
              {name?.charAt(0)}
            </Avatar>
          )}
          <div>
            <h3 className="text-slate-800 mb-0.5 font-medium">
              {isReady ? name : <Skeleton width={50} />}
            </h3>
            <h4 className="text-stone-500 text-sm font-medium tracking-widest">{pan}</h4>
          </div>
        </div>
        {/* Email ID */}
        <div className="lg:text-right col-span-3 lg:col-span-1">
          <h3 className="text-stone-500 font-semibold text-xs uppercase mb-1">Email ID</h3>
          <h4 className="text-sm font-medium text-slate-800 break-all">
            {isReady ? email || '-' : <Skeleton width={70} className="ml-auto" />}
          </h4>
        </div>
        {/* Phone */}
        <div className="lg:text-right col-span-3 lg:col-span-1">
          <h3 className="text-stone-500 font-semibold text-xs uppercase mb-1">Phone No.</h3>
          <h4 className="text-sm font-medium text-slate-800">
            {isReady ? phone || '-' : <Skeleton width={70} className="ml-auto" />}
          </h4>
        </div>
        {/* Aadhaar */}
        <div className="lg:text-right col-span-3 lg:col-span-1">
          <h3 className="text-stone-500 font-semibold text-xs uppercase mb-1">Aadhaar</h3>
          <h4 className="text-sm font-medium text-slate-800">
            {isReady ? (
              aadhaar ? (
                `**** **** ${aadhaar}`
              ) : (
                '-'
              )
            ) : (
              <Skeleton width={70} className="ml-auto" />
            )}
          </h4>
        </div>
        {/* Bank Details */}
        <div className="lg:text-right col-span-3 lg:col-span-1">
          <h3 className="text-stone-500 font-semibold text-xs uppercase mb-1">Bank Details</h3>
          <button onClick={handleOpen} className="text-sm font-medium text-green-700 underline">
            Click to view
          </button>
        </div>
      </div>

      {/* Investment Summary */}
      <div className="mt-6 mb-8 md:mb-12 grid grid-cols-2 xl:grid-cols-4 border border-stone-300 rounded-lg">
        <div className="p-4 md:p-6 border-r border-r-stone-300 max-xl:border-b max-xl:border-b-stone-300">
          <h3 className="text-stone-700 font-semibold text-xs md:uppercase mb-1">Wallet balance</h3>
          <p className="text-lg md:text-2xl font-medium text-stone-700 tracking-wider">
            {isStatsReady ? getRupee.format(walletBalance || 0) : <Skeleton width={60} />}
          </p>
        </div>
        <div className="p-4 md:p-6 xl:border-r xl:border-r-stone-300 max-xl:border-b max-xl:border-b-stone-300">
          <h3 className="text-stone-700 font-semibold text-xs md:uppercase mb-1">
            Active investments
          </h3>
          <p className="text-lg md:text-2xl font-medium text-stone-700">
            {isStatsReady ? getRupee.format(activeInvestment || 0) : <Skeleton width={60} />}
          </p>
        </div>
        <div className="p-4 md:p-6 border-r border-r-stone-300">
          <h3 className="text-stone-700 font-semibold text-xs md:uppercase mb-1">
            Total investments
          </h3>
          <p className="text-lg md:text-2xl font-medium text-stone-700">
            {isStatsReady ? getRupee.format(totalInvestment || 0) : <Skeleton width={60} />}
          </p>
        </div>
        <div className="p-4 md:p-6">
          <h3 className="text-stone-700 font-semibold text-xs md:uppercase mb-1">
            Total payouts till date
          </h3>
          <p className="text-lg md:text-2xl font-medium text-stone-700">
            {isStatsReady ? getRupee.format(totalPayout || 0) : <Skeleton width={60} />}
          </p>
        </div>
      </div>
      <Tabs
        className="mt-8 max-w-7xl"
        classes={{
          indicator: '!bg-green-700',
          flexContainer: 'border-b border-b-black/[0.08]',
        }}
        value={tab}
        onChange={(e) => handleTabChange((e.target as HTMLButtonElement).id)}
        aria-label="deals-tabs"
      >
        {profileData?.productTypes?.enableInvoiceDiscounting && (
          <Tab
            label="Invoice Discounting"
            id="INVOICE_DISCOUNTING"
            value="INVOICE_DISCOUNTING"
            className="!capitalize !font-medium"
            classes={{
              selected: '!text-green-700',
            }}
            aria-controls="deals-tabpanel-0"
          />
        )}
        {profileData?.productTypes?.enableBond && (
          <Tab
            label="Bonds"
            id="BOND"
            value="BOND"
            className="!capitalize !font-medium"
            classes={{
              selected: '!text-green-700',
            }}
            aria-controls="deals-tabpanel-1"
          />
        )}
        {profileData?.productTypes?.enableAssetLeasing && (
          <Tab
            label="Asset Leasing"
            id="ASSET_LEASING"
            value="ASSET_LEASING"
            className="!capitalize !font-medium"
            classes={{
              selected: '!text-green-700',
            }}
            aria-controls="deals-tabpanel-0"
          />
        )}
        {profileData?.productTypes?.enableTapReserve && (
          <Tab
            label="Tap Reserve"
            id="TAP_RESERVE"
            value="TAP_RESERVE"
            className="!capitalize !font-medium"
            classes={{
              selected: '!text-green-700',
            }}
            aria-controls="deals-tabpanel-1"
          />
        )}
        {profileData?.productTypes?.enableWallet && (
          <Tab
            label="Wallet Transactions"
            className="!capitalize !font-medium"
            classes={{
              selected: '!text-green-700',
            }}
            id="WALLET"
            value="WALLET"
            aria-controls="deals-tabpanel-2"
          />
        )}
        <Tab
          label="Digital Gold"
          className="!capitalize !font-medium"
          classes={{
            selected: '!text-green-700',
          }}
          id="DIGITAL_GOLD_SIP"
          value="DIGITAL_GOLD_SIP"
          aria-controls="deals-tabpanel-2"
        />
        <Tab
          label="Digital Silver"
          className="!capitalize !font-medium"
          classes={{
            selected: '!text-green-700',
          }}
          id="DIGITAL_SILVER_SIP"
          value="DIGITAL_SILVER_SIP"
          aria-controls="deals-tabpanel-2"
        />
      </Tabs>
      <div className="flex flex-col justify-start gap-3 items-start md:flex-row md:justify-between md:items-center mt-6 mb-4">
        <div className="flex gap-3 items-center">
          {tab !== 'WALLET' && tab !== 'DIGITAL_GOLD_SIP' && tab !== 'DIGITAL_SILVER_SIP' && (
            <Input
              prefixIcon={<SearchOutlined />}
              placeholder="Search"
              onChange={debounceHandleSearch}
              className="ps-11"
            />
          )}
          {(tab === 'DIGITAL_GOLD_SIP' || tab === 'DIGITAL_SILVER_SIP') && (
            <>
              <TransactionTypeDropdown
                options={
                  tab === 'DIGITAL_GOLD_SIP'
                    ? [
                        { label: 'Gold Withdraw', value: 'GOLD_WITHDRAW' },
                        { label: 'Gold Recharge', value: 'GOLD_RECHARGE' },
                        { label: 'Gold Reward', value: 'GOLD_REWARD' },
                        { label: 'Gold Saving Daily', value: 'GOLD_SAVING_DAILY' },
                        { label: 'Gold Saving Weekly', value: 'GOLD_SAVING_WEEKLY' },
                        { label: 'Gold Saving Monthly', value: 'GOLD_SAVING_MONTHLY' },
                      ]
                    : [
                        { label: 'Silver Withdraw', value: 'SILVER_WITHDRAW' },
                        { label: 'Silver Recharge', value: 'SILVER_RECHARGE' },
                        { label: 'Silver Reward', value: 'SILVER_REWARD' },
                        { label: 'Silver Saving Daily', value: 'SILVER_SAVING_DAILY' },
                        { label: 'Silver Saving Weekly', value: 'SILVER_SAVING_WEEKLY' },
                        { label: 'Silver Saving Monthly', value: 'SILVER_SAVING_MONTHLY' },
                      ]
                }
                value={transactionTypeFilter}
                onChange={setTransactionTypeFilter}
                placeholder="Filter by type"
              />
              {tab === 'DIGITAL_GOLD_SIP' && (
                <div className="flex flex-row gap-2">
                  <p>Gold in gms:</p>
                  {metalSipDetailsLoading ? (
                    <Skeleton width={80} />
                  ) : (
                    <p className="text-green-800">{metalSipDetails?.goldInGms || 0} gms</p>
                  )}
                </div>
              )}
              {tab === 'DIGITAL_SILVER_SIP' && (
                <div className="flex flex-row gap-2">
                  <p>Silver in gms:</p>
                  {metalSipDetailsLoading ? (
                    <Skeleton width={80} />
                  ) : (
                    <p className="text-green-800">{metalSipDetails?.silverInGms || 0} gms</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex gap-2 items-center">
          {tab !== 'WALLET' && (
            <button
              onClick={() => {
                downloadCsv();
                track({
                  eventName: CLIENT_DETAIL_EVENTS.Client_Overview_Download_Excel,
                });
              }}
              disabled={
                tab === 'DIGITAL_GOLD_SIP' || tab === 'DIGITAL_SILVER_SIP'
                  ? metalsLedgerData?.data?.meta?.totalElements < 1 || metalsLedgerLoading
                  : currentData?.data?.content.length < 1 || csvLoading || currentLoading
              }
              type="button"
              className={`${
                tab === 'DIGITAL_GOLD_SIP' || tab === 'DIGITAL_SILVER_SIP'
                  ? metalsLedgerLoading
                    ? 'bg-green-800/60 cursor-not-allowed'
                    : 'bg-green-800'
                  : currentData?.data?.content.length < 1 || csvLoading || currentLoading
                    ? 'bg-green-800/60 cursor-not-allowed'
                    : 'bg-green-800'
              } shadow border border-slate-300 flex items-center gap-2 text-sm text-white rounded-lg font-semibold h-10 w-fit px-4`}
            >
              Download Excel
              <DownloadForOfflineOutlinedIcon fontSize="small" />
            </button>
          )}
        </div>
      </div>
      {/* Client Investments Table */}
      {isInvestmentsReady &&
      !investmentsLoading &&
      (tab === 'INVOICE_DISCOUNTING' ||
        tab === 'BOND' ||
        tab === 'TAP_RESERVE' ||
        tab === 'ASSET_LEASING') ? (
        <ClientInvestmentsTable {...clientInvestmentsTableProps} />
      ) : tab === 'WALLET' && !goldInvestmentsLoading && profileData?.productTypes?.enableWallet ? (
        <TransactionList content={goldInvestmentsData?.data?.content || []} />
      ) : tab === 'DIGITAL_GOLD_SIP' || tab === 'DIGITAL_SILVER_SIP' ? (
        <DigitalMetalSipTransactionsTable
          clientId={clientId}
          tab={tab}
          search={search}
          transactionTypeFilter={transactionTypeFilter}
        />
      ) : (
        <TableSkeleton />
      )}

      {/* Tax Report FY Selection Modal */}
      <Dialog
        maxWidth="xs"
        fullWidth
        onClose={() => setTaxReportModalOpen(false)}
        open={taxReportModalOpen}
      >
        <div className="p-6 bg-white rounded-lg">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-base font-bold text-stone-900">Download Tax Report</h3>
            <button
              onClick={() => setTaxReportModalOpen(false)}
              className="text-stone-400 hover:text-stone-600 text-xl leading-none"
            >
              &times;
            </button>
          </div>
          <p className="text-sm text-stone-500 mb-4">
            Select financial year for <strong>{name}</strong>
          </p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            {[2026, 2025, 2024].map((fy) => (
              <button
                key={fy}
                onClick={() => setSelectedFY(fy)}
                className={`border-2 rounded-lg p-3 text-left transition-all ${
                  selectedFY === fy
                    ? 'border-green-700 bg-green-50'
                    : 'border-stone-200 hover:border-green-300 hover:bg-green-50/50'
                }`}
              >
                <div className="text-sm font-bold text-stone-900">
                  FY {fy - 1}–{fy}
                </div>
                <div className="text-xs text-stone-500 mt-0.5">
                  Apr {fy - 1} – Mar {fy}
                </div>
              </button>
            ))}
          </div>
          <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-xs text-stone-500 mb-4">
            ℹ️ Report includes TDS deducted, gross interest earned, and net payout details for the
            selected period.
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setTaxReportModalOpen(false)}
              className="border border-stone-300 bg-white text-stone-700 text-sm font-semibold px-4 py-2 rounded-lg hover:bg-stone-50"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (alTaxReportsGenerated || idTaxReportGenerated) {
                  downloadTaxReport();
                  setTaxReportModalOpen(false);
                } else {
                  toast.error('No report available currently for download');
                }
              }}
              disabled={taxReportLoading}
              className="bg-green-700 text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-800 disabled:opacity-60"
            >
              <DownloadForOfflineOutlinedIcon fontSize="small" />
              {taxReportLoading ? 'Downloading...' : 'Download Report'}
            </button>
          </div>
        </div>
      </Dialog>

      {/* Bank Details */}
      <Dialog maxWidth="sm" fullWidth onClose={handleClose} open={open}>
        <div className="p-5 border bg-white border-zinc-200 rounded-lg">
          {isBankLoading ? (
            <Skeleton width={80} height={40} />
          ) : (
            <h3 className="text-lg font-semibold mb-4">Bank Details</h3>
          )}
          <div>
            <div>
              <h3 className="text-stone-700 font-semibold text-xs uppercase mb-1">
                Account number
              </h3>
              <p className="text-sm font-medium text-stone-700">{accountNumber || '-'}</p>
            </div>

            <div className="mt-5">
              <h3 className="text-stone-700 font-semibold text-xs uppercase mb-1">IFSC Code</h3>
              <p className="text-sm font-medium text-stone-700">{ifscCode || '-'}</p>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Beneficiary Details  */}
      <Dialog
        maxWidth="sm"
        fullWidth
        onClose={handleViewBeneficiaryClose}
        open={viewBeneficiaryOpen}
      >
        <div className="py-5 border bg-white border-zinc-200 rounded-lg">
          <div className="px-5 border-b border-b-black/10 pb-6">
            {virtualAccountLoading ? (
              <Skeleton width={80} height={40} />
            ) : (
              <h3 className="text-lg font-semibold">Beneficiary Details</h3>
            )}
            <p className="text-slate-600 text-sm mt-1.5">
              Deposit amount into this bank account to update wallet balance for investments.
            </p>
          </div>
          <div className="p-5 flex flex-col gap-3">
            <div className="flex items-center">
              <h4 className="text-sm text-gray-600 w-36">Name:</h4>
              <h3 className="text-sm font-medium">Fixdot Fintech Private Limited</h3>
            </div>
            <div className="flex items-center">
              <h4 className="text-sm text-gray-600 w-36">Bank Name:</h4>
              <h3 className="text-sm font-medium">{walletBankName}</h3>
            </div>
            <div className="flex items-center">
              <h4 className="text-sm text-gray-600 w-36">Branch Name:</h4>
              <h3 className="text-sm font-medium">{walletBranchDetails}</h3>
            </div>
            <div className="flex items-center">
              <h4 className="text-sm text-gray-600 w-36">Account Number:</h4>
              <h3 className="numeric-inter text-sm font-medium">{walletAccountNumber}</h3>
            </div>
            <div className="flex items-center">
              <h4 className="text-sm text-gray-600 w-36">IFSC Code:</h4>
              <h3 className="numeric-inter text-sm font-medium">{walletIfscCode}</h3>
            </div>
          </div>
          <div className="px-5 pt-6 border-t border-black/10">
            <div className="flex items-center gap-3 justify-end">
              <a
                href={`whatsapp://send?text=${waText}`}
                target="_blank"
                rel="noreferrer"
                className="bg-white text-slate-600 border border-black/10 shadow flex items-center gap-2 text-sm rounded-xl font-semibold px-4 h-10"
              >
                Share via WhatsApp
              </a>
              <button
                onClick={copyToClipboard}
                className="bg-green-700 text-white text-sm font-semibold shadow h-10 px-4 rounded-xl"
              >
                Copy All
              </button>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Wallet Details Open */}
      <Dialog maxWidth="sm" fullWidth onClose={handleWalletDetailsClose} open={walletDetailsOpen}>
        <div className="py-5 border bg-white border-zinc-200 rounded-lg">
          <div className="px-5 border-b border-b-black/10 pb-6 flex justify-between">
            <div>
              {virtualAccountLoading ? (
                <Skeleton width={80} height={40} />
              ) : (
                <h3 className="text-lg font-semibold">Wallet Details</h3>
              )}
              <p className="text-slate-600 text-sm mt-1.5">
                Your client needs to add money to the following beneficiary, to make investments.
              </p>
            </div>
            <CloseOutlinedIcon
              className="cursor-pointer bg-slate-200 rounded-full p-1"
              onClick={handleWalletDetailsClose}
              style={{
                width: '28px',
                height: '28px',
              }}
            />
          </div>
          <div className="p-5 flex flex-col gap-3">
            <div className="flex items-center">
              <h4 className="text-sm text-gray-600 w-36">Current Balance:</h4>
              <h3 className="numeric-inter text-sm font-medium text-green-700 tracking-wider">
                {isStatsReady ? getRupee.format(walletBalance || 0) : <Skeleton width={60} />}
              </h3>
            </div>
            <div className="flex items-center">
              <h4 className="text-sm text-gray-600 w-36">Name:</h4>
              <h3 className="text-sm font-medium">{name}</h3>
            </div>
            <div className="flex items-center">
              <h4 className="text-sm text-gray-600 w-36">Bank Name:</h4>
              <h3 className="text-sm font-medium">{walletBankName}</h3>
            </div>
            <div className="flex items-center">
              <h4 className="text-sm text-gray-600 w-36">Branch Name:</h4>
              <h3 className="text-sm font-medium">{walletBranchDetails}</h3>
            </div>
            <div className="flex items-center">
              <h4 className="text-sm text-gray-600 w-36">Account Number:</h4>
              <h3 className="numeric-inter text-sm font-medium">{walletAccountNumber}</h3>
            </div>
            <div className="flex items-center">
              <h4 className="text-sm text-gray-600 w-36">IFSC Code:</h4>
              <h3 className=" numeric-inter text-sm font-medium">{walletIfscCode}</h3>
            </div>
          </div>
          <div className="px-5 pt-6 border-t border-black/10">
            <div className="flex items-center gap-3 justify-end">
              <Button
                className="!bg-white !text-slate-600 !border !border-black/10 !shadow !flex !items-center !text-sm !rounded-xl !font-semibold !px-4 !h-10"
                startIcon={<PlayForWorkIcon />}
                onClick={() => {
                  handleWithdrawWalletBalanceOpen();
                }}
              >
                Withdraw Money
              </Button>
              <Button
                onClick={() => {
                  handleAddWalletBalanceOpen();
                }}
                disabled={!isOnlinePaymentEnabled}
                className="!bg-green-700 !text-white !text-sm !font-semibold !shadow !h-10 !px-4 !rounded-xl disabled:!bg-gray-500 disabled:!pointer-events-auto disabled:!cursor-not-allowed"
                startIcon={<AddCircleIcon />}
              >
                Add Money
              </Button>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Add Money in wallet Modal */}
      <Dialog maxWidth="md" fullWidth onClose={handleAddWalletBalanceClose} open={addWalletOpen}>
        <div className="py-5 border bg-white border-zinc-200 border-b-0">
          <div className="px-5 border-b border-b-black/10 pb-6 flex justify-between">
            <div>
              {virtualAccountLoading ? (
                <Skeleton width={80} height={40} />
              ) : (
                <h3 className="text-lg font-semibold">Recharge your client's wallet</h3>
              )}
              <p className="text-slate-600 text-sm mt-1.5">
                A payment link will be created and sent to your client, allowing them to add money
                to their wallet
              </p>
            </div>
            <CloseOutlinedIcon
              className="cursor-pointer bg-slate-200 rounded-full p-1"
              onClick={() => handleAddWalletBalanceClose()}
              style={{
                width: '28px',
                height: '28px',
              }}
            />
          </div>
          <div className="flex justify-between px-10 py-8 items-center">
            <div className="flex flex-col gap-5">
              <h2 className="text-gray-700 font-medium text-lg">Your client's details</h2>
              <div className="flex items-center">
                <h4 className="text-md text-gray-700 w-36 opacity-40">Current Balance</h4>
                <h3 className="numeric-inter text-base font-normal text-green-700 opacity-80 tracking-wider">
                  {isStatsReady ? getRupee.format(walletBalance || 0) : <Skeleton width={60} />}
                </h3>
              </div>
              <div className="flex items-center">
                <h4 className="text-md text-gray-700 w-36 opacity-40">Pan Number</h4>
                <h3 className="text-base font-normal text-stone-800 opacity-80">{pan}</h3>
              </div>
              <div className="flex items-center">
                <h4 className="text-md text-gray-700 w-36 opacity-40">Phone Number</h4>
                <h3 className="text-base font-normal text-stone-800 opacity-80">{phone}</h3>
              </div>
              <div className="flex items-center">
                <h4 className="text-md text-gray-700 w-36 opacity-40">Email ID</h4>
                <h3 className="text-base font-normal text-stone-800 opacity-80">{email}</h3>
              </div>
            </div>

            {/* input */}
            <div className="flex flex-col border rounded-md border-stone-300 h-fit w-2/5 ">
              <h2 className="text-gray-700 font-semibold text-sm mx-5 my-3">Enter Amount</h2>

              <div className="relative w-full mb-6 ml-3 ">
                <span className="absolute left-1 top-1/2 transform -translate-y-1/2 text-gray-500">
                  ₹
                </span>
                <input
                  type="text"
                  value={amount}
                  onChange={handleAmountChange}
                  placeholder="1,00,000"
                  className="numeric-inter ml-4 text-lg font-medium text-stone-800 outline-none"
                  onFocusCapture={() => {
                    track({
                      eventName: CLIENT_DETAIL_EVENTS.Client_Wallet_Enter_Amount,
                    });
                  }}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col px-10 gap-4">
            {paymentGateway !== PAYMENT_GATEWAY_IDFC && (
              <div className="flex gap-2 items-center bg-[#f2901f14] rounded-lg px-3 py-4 w-full">
                <InfoOutlinedIcon className="text-[#D97706]" fontSize="small" />
                <span className="text-xs text-neutral-600">
                  Transactions made via UPI will take 24-48 working hours to reflect in your wallet.
                </span>
              </div>
            )}
            {paymentGateway === PAYMENT_GATEWAY_IDFC && (
              <div className="flex gap-2 items-center bg-gradient-to-r from-[#E7FFED] to-[#A3FFBA] border border-[#00D20080] rounded-lg px-3 py-4 w-full">
                <InfoOutlinedIcon className="text-stone-700" fontSize="small" />
                <span className="text-xs text-stone-700">
                  We have updated our UPI transaction flow. Going forward, all UPI transactions will
                  be reflected <b>Instantly</b>.
                </span>
              </div>
            )}
            {/* <div className="flex gap-2">
              <AccountBalanceWalletIcon className="text-stone-900" fontSize="small" />
              <span className="text-sm text-neutral-600">
                Recharge your wallet via <b className="text-stone-900">IMPS, NEFT or RTGS</b> to
                receive funds <b className="text-stone-900">faster.</b>
              </span>
            </div> */}
          </div>
        </div>
        <div className="px-5 py-6 border-t border-black/10">
          <div className="flex items-center gap-3 justify-end">
            <button
              onClick={handleGenerateLink}
              className={`${
                Number(amount) > 0 && isTopUpAllowed
                  ? 'bg-green-700'
                  : 'bg-gray-300 cursor-not-allowed'
              } text-white text-sm font-semibold h-[2.65rem] px-4 rounded-xl `}
              disabled={!amount || Number(amount) === 0 || !isTopUpAllowed}
            >
              Generate Link
            </button>
          </div>
        </div>
      </Dialog>

      {/* Withdraw Money from wallet Modal */}
      <Dialog
        maxWidth="md"
        fullWidth
        onClose={handleWithdrawWalletBalanceClose}
        open={withdrawWalletOpen}
      >
        <div className="py-5 border bg-white border-zinc-200 border-b-0">
          <div className="px-5 border-b border-b-black/10 pb-6 flex justify-between">
            <div>
              {virtualAccountLoading ? (
                <Skeleton width={80} height={40} />
              ) : (
                <h3 className="text-lg font-semibold">Withdraw Money</h3>
              )}
              <p className="text-slate-600 text-sm mt-1.5">
                Enter the amount below to proceed withdrawing money from your client's wallet
              </p>
            </div>
            <CloseOutlinedIcon
              className="cursor-pointer bg-slate-200 rounded-full p-1"
              onClick={handleWithdrawWalletBalanceClose}
              style={{
                width: '28px',
                height: '28px',
              }}
            />
          </div>
          <div className="flex justify-between px-10 py-8 items-center">
            <div className="flex flex-col gap-5">
              <h2 className="text-gray-700 font-medium text-lg">Your client's details</h2>
              <div className="flex items-center">
                <h4 className="text-md text-gray-700 w-36 opacity-40">Current Balance</h4>
                <h3 className="numeric-inter text-base font-normal text-green-700 opacity-80 tracking-wider">
                  {isStatsReady ? getRupee.format(walletBalance || 0) : <Skeleton width={60} />}
                </h3>
              </div>
              <div className="flex items-center">
                <h4 className="text-md text-gray-700 w-36 opacity-40">Pan Number</h4>
                <h3 className="text-base font-normal text-stone-800 opacity-80">{pan}</h3>
              </div>
              <div className="flex items-center">
                <h4 className="text-md text-gray-700 w-36 opacity-40">Phone Number</h4>
                <h3 className="text-base font-normal text-stone-800 opacity-80">{phone}</h3>
              </div>
              <div className="flex items-center">
                <h4 className="text-md text-gray-700 w-36 opacity-40">Email ID</h4>
                <h3 className="text-base font-normal text-stone-800 opacity-80">{email}</h3>
              </div>
            </div>

            {/* input */}
            <div className="flex flex-col border rounded-md border-stone-300 h-fit w-2/5 ">
              <h2 className="text-gray-700 font-semibold text-sm mx-5 my-3">Enter Amount</h2>

              <div className="relative w-full mb-6 ml-3 ">
                <span className="absolute left-1 top-1/2 transform -translate-y-1/2 text-gray-500">
                  ₹
                </span>
                <input
                  type="text"
                  value={withdrawAmount}
                  onChange={handleWithdrawAmountChange}
                  placeholder="10,000"
                  className="numeric-inter ml-4 text-lg font-medium text-stone-800 outline-none"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="px-5 py-6 border-t border-black/10">
          <div className="flex items-center gap-3 justify-end">
            <button
              onClick={handleWithdrawWalletBalanceClose}
              className={`border-slate-200 border bg-white text-sm font-semibold h-[2.65rem] px-4 rounded-xl text-slate-600 `}
            >
              Dismiss
            </button>
            <Button
              onClick={() => handleWithdrawMoney()}
              className={`${
                Number(withdrawAmount) > 0 && !withdrawLoading
                  ? '!bg-green-700'
                  : '!bg-gray-300 !cursor-not-allowed'
              } !text-white !text-sm !font-semibold !h-[2.65rem] !px-4 !rounded-xl `}
              disabled={!withdrawAmount || Number(withdrawAmount) === 0 || withdrawLoading}
              endIcon={withdrawLoading && <CircularProgress size={20} color="success" />}
            >
              Withdraw
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Client Generate Payment Link */}
      <Dialog open={isPaymentOpen} maxWidth="sm" fullWidth>
        <div className="flex bg-amber-100 p-2.5 items-center gap-2 ">
          <InfoIcon
            className="text-amber-700"
            sx={{
              height: '16px',
              width: '16px',
            }}
          />
          <h4 className="text-sm text-amber-700 font-medium">
            This link is valid only for 6 hours
          </h4>
        </div>
        <DialogTitle>
          <div className="px-5 flex justify-between">
            <div>
              <h3 className="text-lg font-semibold">Link Generated</h3>
              <p className="text-slate-600 text-sm mt-1.5">
                Copy link to receive money in the client's wallet
              </p>
            </div>
            <CloseOutlinedIcon
              className="cursor-pointer bg-slate-200 rounded-full p-1"
              onClick={() => handlePaymentClose()}
              style={{
                width: '28px',
                height: '28px',
              }}
            />
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="py-5bg-white rounded-lg">
            <div className="p-5 pb-3 flex gap-3">
              <div
                className="flex flex-col justify-between gap-[6px]  flex-1"
                style={{
                  padding: '14px 16px 16px 0px',
                  borderRight: '1px solid #f0f0f0',
                }}
              >
                <h4 className="text-sm text-stone-400 ">Wallet top-up Amount </h4>
                <h3 className="text-base font-medium text-green-700 tracking-wider">₹{amount}</h3>
              </div>
              <div
                className="flex flex-col justify-between gap-[6px] flex-1"
                style={{ padding: '14px 16px 16px 24px' }}
              >
                <h4 className="text-sm text-stone-400">Current Balance</h4>
                <h3 className="text-base font-medium tracking-wider">
                  {getRupee.format(walletBalance || 0)}
                </h3>
              </div>
            </div>

            <div className="px-5 pt-6 pb-3">
              {paymentGateway !== PAYMENT_GATEWAY_IDFC && (
                <div className="flex gap-2 items-center bg-[#f2901f14] rounded-lg px-3 py-4 w-full mb-8">
                  <InfoOutlinedIcon className="text-[#D97706]" fontSize="small" />
                  <span className="text-xs text-neutral-600">
                    Transactions made via UPI will take{' '}
                    <b className="text-stone-900">24-48 working hours</b> to reflect in your wallet.
                    Recharge your wallet via <b className="text-stone-900">IMPS, NEFT or RTGS</b> to
                    receive funds <b className="text-stone-900">faster.</b>
                  </span>
                </div>
              )}
              {paymentGateway === PAYMENT_GATEWAY_IDFC && (
                <div className="flex gap-2 items-center bg-gradient-to-r from-[#E7FFED] to-[#A3FFBA] border border-[#00D20080] rounded-lg px-3 py-2 w-full">
                  <InfoOutlinedIcon className="text-stone-700" fontSize="small" />
                  <span className="text-[10px] text-stone-700">
                    We have updated our UPI transaction flow. Going forward, all UPI transactions
                    will be reflected <b>Instantly</b>.
                  </span>
                </div>
              )}
              {paymentGateway === PAYMENT_GATEWAY_IDFC && (
                <p className="text-[10px] text-stone-700 my-6">
                  A convenience fee of <b>₹{convenienceFee}</b> applies to payments made via UPI. To
                  avoid this charge, please choose Bank Transfer as the payment method.
                </p>
              )}
              <div className="flex flex-col gap-[6px]">
                <h4 className="text-sm text-gray-600">Share link with your client</h4>
                <div className="flex items-center w-full">
                  <input
                    type="text"
                    value={`${userPaymentLink || 'loading link...'}`}
                    readOnly
                    className="flex-grow border border-gray-300 rounded-l-xl p-2"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(userPaymentLink);
                      track({
                        eventName: CLIENT_DETAIL_EVENTS.Client_Copy_url,
                      });
                      toast.success('Link copied to clipboard', {
                        duration: 2000,
                        position: 'top-center',
                      });
                    }}
                    className="bg-green-700 text-white text-sm font-semibold h-[2.65rem] px-4 rounded-r-xl "
                  >
                    Copy Url
                  </button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <ReinvestModal />
    </div>
  );
};

ClientDetail.displayName = 'ClientDetail';

export default ClientDetail;
